import * as React from "react";

import {
  extractPricingInformationFromProductInterface,
  GroupedProduct,
  GroupedProductItem,
  isGroupedProduct,
  ProductInterface,
  ProductInterface_PricingInformation,
  useDataMutationAddToWishlist,
} from "../../../Common";

interface PdpWishlistPresentationConsumerInterface {
  presentationProducts: PresentationAddedWishlistItem[];
}
const defaultValue = {} as unknown as PdpWishlistPresentationConsumerInterface;
const PdpWishlistPresentationContext = React.createContext(defaultValue);

export type PresentationAddedWishlistItem = ProductInterface &
  GroupedProduct & {
    quantityWishedToAdd: number;
    pricingInformation: ProductInterface_PricingInformation;
  };

export const PdpWishlistPresentationProvider: React.FunctionComponent = (
  props
) => {
  const { data, request } = useDataMutationAddToWishlist();

  const presentationProducts = React.useMemo(() => {
    let itemsToPresent: PresentationAddedWishlistItem[] = [];
    (data?.wishlist?.items || []).forEach((responseItem) => {
      const { sku, parent_sku } =
        responseItem.bbnt_custom_wishlist_item_option || {};

      // early out if this item isn't one of the ones we just added
      const matchingWishlistItemThatWeIntendedToAdd = (
        request?.addTheseItemsToWishlist || []
      ).find(({ sku: expectedSku, parent_sku: expectedParentSku }) => {
        if (expectedParentSku !== null && expectedParentSku !== undefined) {
          return (
            sku === `${expectedSku}` && parent_sku === `${expectedParentSku}`
          );
        }
        return sku === `${expectedSku}`;
      });
      if (!matchingWishlistItemThatWeIntendedToAdd) {
        return;
      }

      // find the product that we're expecting
      let product: ProductInterface | GroupedProduct =
        responseItem.product || ({} as ProductInterface);
      if (parent_sku) {
        let matchingWishlistitemThatWeIntendedToAddParentSku;
        if (
          matchingWishlistItemThatWeIntendedToAdd.parent_sku !== null &&
          matchingWishlistItemThatWeIntendedToAdd.parent_sku !== undefined
        ) {
          matchingWishlistitemThatWeIntendedToAddParentSku = `${matchingWishlistItemThatWeIntendedToAdd.parent_sku}`;
        }
        if (
          parent_sku !== product.sku ||
          product.sku !== matchingWishlistitemThatWeIntendedToAddParentSku
        ) {
          return;
        }

        let thisProductsChildItems: GroupedProductItem[] = [];
        if (isGroupedProduct(product)) {
          thisProductsChildItems = product.items || [];
        }
        if (thisProductsChildItems.length < 1) {
          return;
        }

        const matchingChildItem = thisProductsChildItems.find(
          ({ product }) =>
            product &&
            product.sku === sku &&
            `${matchingWishlistItemThatWeIntendedToAdd.sku}` === product.sku
        );
        if (!matchingChildItem) {
          return;
        }

        const matchingChildProduct = matchingChildItem.product;
        if (!matchingChildProduct) {
          return;
        }

        product = matchingChildProduct;
      } else {
        if (
          product.sku !== sku ||
          `${matchingWishlistItemThatWeIntendedToAdd.sku}` !== product.sku
        ) {
          return;
        }
      }

      const pricingInformation =
        extractPricingInformationFromProductInterface(product);

      const thisItem: PresentationAddedWishlistItem = {
        ...product,
        ...{
          quantityWishedToAdd: matchingWishlistItemThatWeIntendedToAdd.quantity,
          pricingInformation,
        },
      };

      itemsToPresent = [...itemsToPresent, ...[thisItem]];
    });
    return itemsToPresent;
  }, [data, request]);

  const value: PdpWishlistPresentationConsumerInterface = React.useMemo(
    () => ({
      presentationProducts,
    }),
    [presentationProducts]
  );
  return (
    <PdpWishlistPresentationContext.Provider value={value}>
      {props.children || []}
    </PdpWishlistPresentationContext.Provider>
  );
};

export const usePdpWishlistPresentation = () => {
  const context = React.useContext(PdpWishlistPresentationContext);
  if (context === defaultValue) {
    throw new Error(
      "usePdpWishlistPresentation must be used within a PdpWishlistPresentationProvider"
    );
  }
  return context;
};
